import React, { useState, useEffect } from 'react';
import styles from '../Editor/MarkdownEditor.module.css';
import { FolderPathAutocomplete } from './FolderPathAutocomplete';
import { docsApi } from '../../services/api';

interface DocumentInfoProps {
  title: string;
  setTitle: (title: string) => void;
  folderPath: string;
  setFolderPath: (path: string) => void;
  isPrivate: boolean;
  setIsPrivate: (isPrivate: boolean) => void;
  tags: string[];
  tagInput: string;
  setTagInput: (tag: string) => void;
  handleAddTag: (e: React.KeyboardEvent) => void;
  handleRemoveTag: (tag: string) => void;
  folderInputRef: React.RefObject<HTMLInputElement>;
  titleInputRef: React.RefObject<HTMLInputElement>;
  error: string;
}

export const DocumentInfo: React.FC<DocumentInfoProps> = ({
  title,
  setTitle,
  folderPath,
  setFolderPath,
  isPrivate,
  setIsPrivate,
  tags,
  tagInput,
  setTagInput,
  handleAddTag,
  handleRemoveTag,
  folderInputRef,
  titleInputRef,
  error,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [allFolders, setAllFolders] = useState<string[]>([]);
  const [filteredFolders, setFilteredFolders] = useState<string[]>([]);
  const [folderAutocompleteSelectedIndex, setFolderAutocompleteSelectedIndex] = useState(0);
  const [showFolderAutocomplete, setShowFolderAutocomplete] = useState(false);
  const [folderAutocompletePosition, setFolderAutocompletePosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const loadFolders = async () => {
      try {
        const response = await docsApi.getFolders();
        setAllFolders(response.data);
      } catch (err) {
        console.error('Failed to load folders for autocomplete:', err);
      }
    };
    loadFolders();
  }, []);

  useEffect(() => {
    if (!folderPath) {
      setFilteredFolders(allFolders);
    } else {
      const pathLower = folderPath.toLowerCase();
      const filtered = allFolders.filter(folder =>
        folder.toLowerCase().includes(pathLower)
      );
      setFilteredFolders(filtered);
    }
    setFolderAutocompleteSelectedIndex(0);
  }, [folderPath, allFolders]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'm' || e.key === 'M')) {
        e.preventDefault();
        setIsCollapsed(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (folderInputRef.current && !folderInputRef.current.contains(e.target as Node)) {
        setShowFolderAutocomplete(false);
      }
    };

    if (showFolderAutocomplete) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFolderAutocomplete]);

  const handleFolderPathFocus = () => {
    if (allFolders.length > 0 && folderInputRef.current) {
      setShowFolderAutocomplete(true);
      const top = folderInputRef.current.offsetTop + folderInputRef.current.offsetHeight + 4;
      const left = folderInputRef.current.offsetLeft;
      setFolderAutocompletePosition({ top, left });
    }
  };

  const handleFolderSelect = (folder: string) => {
    setFolderPath(folder);
    setShowFolderAutocomplete(false);
    folderInputRef.current?.focus();
  };

  const handleFolderPathKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showFolderAutocomplete) {
      if (e.key === 'ArrowDown' && allFolders.length > 0) {
        e.preventDefault();
        setShowFolderAutocomplete(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFolderAutocompleteSelectedIndex((prev) => 
        Math.min(prev + 1, filteredFolders.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFolderAutocompleteSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (filteredFolders[folderAutocompleteSelectedIndex]) {
        e.preventDefault();
        handleFolderSelect(filteredFolders[folderAutocompleteSelectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowFolderAutocomplete(false);
    }
  };

  return (
    <div className={styles.metadataForm}>
      <button
        className={styles.collapseButton}
        onClick={() => setIsCollapsed(!isCollapsed)}
        type="button"
      >
        {isCollapsed ? '▶ Show Metadata' : '▼ Hide Metadata'}
      </button>
      
      {!isCollapsed && (
        <>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Title</label>
            <input
              ref={titleInputRef}
              type="text"
              className={styles.formInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Folder Path</label>
            <div style={{ position: 'relative' }}>
              <input
                ref={folderInputRef}
                type="text"
                className={styles.formInput}
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                onFocus={handleFolderPathFocus}
                onKeyDown={handleFolderPathKeyDown}
                placeholder="/"
              />
              {showFolderAutocomplete && filteredFolders.length > 0 && (
                <FolderPathAutocomplete
                  folders={filteredFolders}
                  selectedIndex={folderAutocompleteSelectedIndex}
                  onSelect={handleFolderSelect}
                  onSelectedIndexChange={setFolderAutocompleteSelectedIndex}
                  position={folderAutocompletePosition}
                />
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Tags</label>
            <div className={styles.tagInputContainer}>
              {tags.map((tag) => (
                <span key={tag} className={styles.tagChip}>
                  {tag}
                  <span
                    className={styles.tagChipRemove}
                    onClick={() => handleRemoveTag(tag)}
                  >
                    ×
                  </span>
                </span>
              ))}
              <input
                type="text"
                className={styles.tagAddInput}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="Add tag and press Enter"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span>Make this document private</span>
            </label>
            <small style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px', display: 'block' }}>
              Private documents are only visible to logged-in users
            </small>
          </div>

          {error && <div className={styles.formError}>{error}</div>}
        </>
      )}
    </div>
  );
};
